import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import {
  GAP_SCALE,
  RADIUS_SCALE,
  SURFACE_PADDING,
  POSITION_SCALE,
  SIZE_SCALE,
  TYPE_SCALE,
  COLOR_SCALE,
  SHADOW_SCALE,
  Z_INDEX,
  TYPE_LEADING,
  ACCESSIBILITY_SCALE,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      `fixed inset-0 z-${Z_INDEX.overlay} ${COLOR_SCALE['dialog-overlay']} backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out`,
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const { t } = useTranslation();
  return (
    <DialogPortal container={typeof document !== 'undefined' ? document.body : undefined}>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          `fixed left-1/2 top-1/2 z-${Z_INDEX.modal} grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 ${GAP_SCALE['4']} ${RADIUS_SCALE['2xl']} border bg-background ${SURFACE_PADDING['4']} sm:${SURFACE_PADDING['6']} md:${SURFACE_PADDING['8']} ${SHADOW_SCALE.lg} duration-150 ease-in-out max-h-[85vh] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]`,
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className={`absolute ${POSITION_SCALE.right['4']} ${POSITION_SCALE.top['4']} ${RADIUS_SCALE.sm} ${SURFACE_PADDING['2']} opacity-70 ring-offset-background transition-opacity duration-150 ease-in-out hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground`}
        >
          <X className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} aria-hidden="true" />
          <span className={`${ACCESSIBILITY_SCALE.srOnly}`}>
            {String(t('common', 'close', 'Close'))}
          </span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(`flex flex-col ${GAP_SCALE.xs} text-center sm:text-left`, className)}
      {...props}
    />
  )
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        `flex flex-col-reverse sm:flex-row sm:justify-end sm:${GAP_SCALE.sm}`,
        className
      )}
      {...props}
    />
  )
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      `${TYPE_SCALE.lg} font-semibold ${TYPE_LEADING.snug} tracking-tight break-words`,
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      `${TYPE_SCALE.sm} text-muted-foreground ${TYPE_LEADING.relaxed} break-words`,
      className
    )}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
